import { useState, useCallback, useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useRouter } from 'src/routes/hooks';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTable } from 'src/hooks/use-table';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { getAllCompanies, deleteCompany, type CompanyDto } from 'src/api/company.api';

import { TableNoData } from '../../employee/table-no-data';
import { TableEmptyRows } from '../../employee/table-empty-rows';
import { CompanyTableRow } from './company-table-row';
import { CompanyTableHead } from './company-table-head';
import { CompanyTableToolbar } from './company-table-toolbar';

const TABLE_HEAD = [
  { id: 'name', label: 'Şirket Adı' },
  { id: 'email', label: 'E-posta' },
  { id: 'address', label: 'Adres' },
  { id: '', label: '' },
];

// Storage keys for pagination persistence
const STORAGE_KEY_PAGE_SIZE = 'qr-admin-table-state:company:pageSize';
const STORAGE_KEY_PAGE = 'qr-admin-table-state:company:page';

// Helper functions for localStorage persistence
const getStoredPageSize = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PAGE_SIZE);
    return stored ? parseInt(stored, 10) : 5;
  } catch {
    return 5;
  }
};

const getStoredPage = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PAGE);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
};

// ----------------------------------------------------------------------

export function CompanyView() {
  const table = useTable();
  const router = useRouter();
  const confirm = useBoolean();
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [filterName, setFilterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<CompanyDto | null>(null);
  
  // Pagination state with localStorage persistence
  const [currentPage, setCurrentPage] = useState(getStoredPage);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  
  // Sorting state
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('name');

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Tüm şirketleri tek seferde çek — arama/sayfalama client-side (şirket sayısı küçük).
      const data = await getAllCompanies(0, 1000, orderBy, order);

      setCompanies(data.content || []);
    } catch (err: any) {
      console.error('Error fetching companies:', err);
      setError(err.message || 'Şirketler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [orderBy, order]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Frontend filtreleme
  const filteredCompanies = useMemo(() => {
    if (!filterName.trim()) {
      return companies;
    }
    
    return companies.filter((company) => 
      company.name.toLowerCase().includes(filterName.toLowerCase()) ||
      company.email.toLowerCase().includes(filterName.toLowerCase()) ||
      company.address.toLowerCase().includes(filterName.toLowerCase())
    );
  }, [companies, filterName]);

  // Client-side sayfalama
  const paginatedCompanies = useMemo(
    () => filteredCompanies.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [filteredCompanies, currentPage, pageSize]
  );

  const notFound = !filteredCompanies.length && !loading;

  const handleFilterByName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentPage(0);
      setFilterName(event.target.value);
    },
    []
  );



  const handleClearSearch = useCallback(() => {
    setFilterName('');
    setCurrentPage(0);
  }, []);

  const handleDeleteRow = useCallback(
    (id: string) => {
      const company = companies.find((comp) => comp.id === id);
      if (company) {
        setCompanyToDelete(company);
        confirm.onTrue();
      }
    },
    [confirm, companies]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (companyToDelete) {
      try {
        await deleteCompany(companyToDelete.id);
        await fetchCompanies();
        confirm.onFalse();
        setCompanyToDelete(null);
      } catch (err: any) {
        console.error('Failed to delete company:', err);
        setError(err.message || 'Şirket silinirken bir hata oluştu.');
        confirm.onFalse();
      }
    }
  }, [companyToDelete, fetchCompanies, confirm]);

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(`/dashboard/companies/${id}/edit`);
    },
    [router]
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    localStorage.setItem(STORAGE_KEY_PAGE, newPage.toString());
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
    localStorage.setItem(STORAGE_KEY_PAGE_SIZE, newPageSize.toString());
    localStorage.setItem(STORAGE_KEY_PAGE, '0');
  }, []);

  const handleSort = useCallback((id: string) => {
    const isAsc = orderBy === id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(id);
  }, [orderBy, order]);

  return (
    <>
      <Container maxWidth={false}>
        <Box display="flex" alignItems="center" mb={5}>
          <Typography variant="h4" flexGrow={1}>
            Şirketler
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => router.push('/dashboard/companies/create')}
          >
            Yeni Şirket
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CompanyTableToolbar
            numSelected={table.selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onClearSearch={handleClearSearch}
          />

          <Scrollbar>
            {loading ? (
              <Typography variant="h6" align="center" sx={{ p: 3 }}>
                Yükleniyor...
              </Typography>
            ) : (
              <TableContainer sx={{ overflow: 'unset' }}>
                <Table sx={{ minWidth: 800 }}>
                                     <CompanyTableHead
                     order={order}
                     orderBy={orderBy}
                     rowCount={paginatedCompanies.length}
                     numSelected={table.selected.length}
                     onSort={handleSort}
                     onSelectAllRows={(checked: boolean) =>
                       table.onSelectAllRows(
                         checked,
                         paginatedCompanies.map((company) => company.id)
                       )
                     }
                     headLabel={TABLE_HEAD}
                   />
                   <TableBody>
                     {paginatedCompanies.map((row) => (
                       <CompanyTableRow
                         key={row.id}
                         row={row}
                         selected={table.selected.includes(row.id)}
                         onSelectRow={() => table.onSelectRow(row.id)}
                         onDeleteRow={() => handleDeleteRow(row.id)}
                         onEditRow={() => handleEditRow(row.id)}
                       />
                     ))}

                     {notFound && <TableNoData searchQuery={filterName} />}
                   </TableBody>
                </Table>
              </TableContainer>
            )}
          </Scrollbar>

                     <TablePagination
             component="div"
             page={currentPage}
             count={filteredCompanies.length}
             rowsPerPage={pageSize}
             onPageChange={handleChangePage}
             rowsPerPageOptions={[5, 10, 25]}
             onRowsPerPageChange={handleChangeRowsPerPage}
             labelRowsPerPage="Sayfa başına satır:"
             labelDisplayedRows={({ from, to, count }) =>
               `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
             }
           />
        </Card>
      </Container>

      <Dialog open={confirm.value} onClose={confirm.onFalse}>
        <DialogTitle>Şirketi Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &quot;{companyToDelete?.name}&quot; adlı şirketi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirm.onFalse} color="inherit">
            İptal
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}